"use client";

import React from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import ProgressBar from '@/components/ProgressBar/ProgressBar';
import {cover3D} from '@/components/BookPreview/BookPreview';

const stepProgressMap = {
  'Order Received': 1.5,
  'In Production': 2.5,
  'Shipped': 3,
};

const OrderDetails = ({ order }) => {
  const router = useRouter();
  const t = useTranslations('myOrders');

  const stepsName = [
    t('orderReceived'),
    t('inProduction'),
    t('shipped'),
  ];

  const {
    orderNumber,
    orderDate,
    book,
    payment,
    recipientName,
    address,
    shippingMethod,
    status,
    trackingId,
    trackingUrl,
    carrier_name: carrier,
  } = order;

  // Map API fields to display variables
  const trackingNumber = trackingId;
  const trackLink = trackingUrl;

  // Format ISO date using a format string like "DD/MM/YYYY", "MM/DD/YYYY", etc.
  const dateObj = new Date(orderDate);
  const dateFormat = t('dateFormat');
  const formattedDate = dateFormat
      .replace('DD', dateObj.getDate().toString().padStart(2, '0'))
      .replace('MM', (dateObj.getMonth() + 1).toString().padStart(2, '0'))
      .replace('YYYY', dateObj.getFullYear().toString());

  // Build payment summary dynamically
  const paymentSummary = [
    { label: t('productPrice'), value: `${payment.currency} ${payment.subtotal}` },
    { label: t('delivery'), value: `${payment.currency} ${payment.shipping}` },
    { label: t('tax'), value: `${payment.currency} ${payment.tax}` },
    { label: t('total'), value: `${payment.currency} ${payment.total}` },
  ];

  const totalSteps = stepsName.length;
  const progressStep = stepProgressMap[status] || 0;
  const currentStep = Math.floor(progressStep);

  const selectedTemplate = {
    front: order.coverFrontPreview ,
    spine: order.coverSpinePreview ,
  }


  const cover = cover3D(selectedTemplate);

  const handleReorder = () => {
    // Store order data in sessionStorage to pass to reorder page
    sessionStorage.setItem('reorderData', JSON.stringify(order));
    router.push('/reorder');
  };

  return (
      <div className="px-4 md:px-0">
        <div className="w-full bg-[#FFFBEE] shadow-detailsShadow pt-5 pb-10 px-4 md:px-10 md:pb-4 border-[0.5px] border-[#A6A6A6] rounded-[3px]">
          <h1 className="text-[18px] font-bold text-center">
            {t('orderNumber')}: <span className="font-normal">{orderNumber}</span>
          </h1>

          {/* Ordered Item */}
          <div className="mt-4 flex flex-col md:flex-row justify-between items-center text-dark gap-4">
            <div className="flex flex-col md:flex-row items-center gap-4 flex-1">
              <div className="transform scale-75">{cover}</div>

              <div className="flex items-center gap-2 text-base leading-[22px]">
                <p className="min-w-[25px]">x {book.quantity}</p>
                <div className="w-fit text-[14px] md:text-base">
                  <p className="font-medium">{book.author}</p>
                  <p className="leading-[18px]">{book.title}</p>
                </div>
              </div>
            </div>

             {/*Reorder Button*/}
            <button
              onClick={handleReorder}
              className="w-full md:w-auto bg-orange hover:bg-[#D89A2E] text-white font-semibold py-3 px-8 rounded-[3px] transition-colors duration-200"
            >
              {t('reorder')} »
            </button>
          </div>

          <hr className="my-6 border-0 h-[0.5px] bg-[#AFAFAF]" />

          <div className="w-full leading-[17px]">
            <div className="flex flex-col md:flex-row justify-between gap-7 md:gap-6">
              {/* Details Section */}
              <div className="order-2 md:order-1 max-w-[400px]">
                <h2 className="font-semibold text-[18px] leading-[27px] text-dark">
                  {t('details')}
                </h2>
                <div className="flex flex-col mt-2 md:mt-5 gap-3">
                  <div className="flex gap-2 text-dark">
                    <span className="font-medium">{t('orderDate')}:</span>
                    <span className="text-dark/70">{formattedDate}</span>
                  </div>
                  <div className="flex gap-2 text-dark">
                    <span className="font-medium">{t('recipientName')}:</span>
                    <span className="text-dark/70 font-normal">{recipientName}</span>
                  </div>
                  <div className="flex gap-2 text-dark">
                    <span className="font-medium">{t('address')}:</span>
                    <span className="text-dark/70 font-normal">
                    {`${address.street1}${address.street2 ? ` ${address.street2}` : ''}, ${address.city}${address.state_code ? `, ${address.state_code}` : ''}, ${address.postcode}`}
                  </span>
                  </div>
                  <div className="flex gap-2 text-dark">
                    <span className="font-medium">{t('shippingMethod')}:</span>
                    <span className="text-dark/70">{t(`shippingTypes.${shippingMethod}`, { defaultValue: shippingMethod })}</span>
                  </div>
                  {carrier && (
                      <div className="flex gap-2 text-dark">
                        <span className="font-medium">{t('carrier')}:</span>
                        <span className="text-dark/70">{carrier}</span>
                      </div>
                  )}
                  {trackingNumber && (
                      <div className="flex gap-2 text-dark">
                        <span className="font-medium">{t('trackingNumber')}:</span>
                        <span className="text-dark/70">{trackingNumber}</span>
                      </div>
                  )}
                  {trackLink && (
                      <div className="flex gap-2 text-dark">
                        <a
                            href={trackLink}
                            className="font-medium text-[#3A65C1] underline"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                          {t('trackShipment')}
                        </a>
                      </div>
                  )}
                </div>
              </div>

              {/* Payment Summary */}
              <div className="order-1 md:order-2 flex-[0.9] md:mt-0">
                <h2 className="font-semibold text-[18px] leading-[27px]">
                  {t('paymentSummary')}
                </h2>
                <div className="flex flex-col mt-2 md:mt-5 gap-3">
                  {paymentSummary.map(({ label, value }) => (
                      <div key={label} className="flex justify-between">
                        <span className="capitalize font-medium">{label}</span>
                        <span className="text-dark/70">{value}</span>
                      </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-10 flex flex-col items-center max-w-[390px] mx-auto">
              <h2 className="font-semibold text-[18px] leading-[38px] text-dark text-center md:text-left mb-4">
                {t('status')}
              </h2>
              <div className="w-full">
                <ProgressBar
                    progressStep={progressStep}
                    currentStep={currentStep}
                    totalSteps={totalSteps}
                    stepsName={stepsName}
                    isMobile={false}
                    myAccount
                />
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default OrderDetails;
